import { NetworkStatus } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useBeforeUnload, useBlocker, useParams } from 'react-router';
import styled from 'styled-components';
import {
  CreateNoteDocument,
  NoteByIdDocument,
  NoteDetailsFragment,
  SelfDocument,
  UpdateNoteDocument,
} from '../../graphql/types';
import useTracking from '../../hooks/useTracking';
import { BORDER_GRAY, WHITE } from '../../theme';
import {
  EventCategories,
  EventLabels,
  EventNames,
  EventProfileCardNames,
  EventSubCategories,
  ScreenNames,
} from '../../utils/trackingEvents';
import LoadingPanel from '../LoadingPanel';
import Button, { ButtonSize, ButtonVariant } from '../reusable/Button';
import { IconButton } from '../reusable/Button/IconButton';
import { ConfirmationModal } from '../reusable/ConfirmationModal';
import { RichTextEditor, RichTextReadOnly } from '../reusable/RichTextEditor';
import { NoteSize, useNotesContext } from './Context';
import { NoteHeader } from './NoteHeader';
import { updateCache } from './utils';

export type NoteType = Pick<
  NoteDetailsFragment,
  'content' | 'title' | 'coach' | 'lastUpdatedBy'
> & { id?: string; updatedAt?: string };

const measuredNoteHeaderHeight = 56;

const EDITOR_HEIGHT = {
  default: 255,
  maximized: 450,
};

const READER_HEIGHT = {
  default: 350,
  maximized: 620,
};

const StyledNoteContainer = styled.div<{
  $open?: boolean;
  $noteSize?: NoteSize;
}>`
  display: grid;
  grid-template-rows: auto 1fr auto;
  grid-template-columns: 100%;

  position: fixed;

  left: 96px;
  bottom: 0;
  z-index: 2;

  width: ${({ $noteSize }) =>
    $noteSize === NoteSize.Maximized ? '500px' : '352px'};

  ${({ $noteSize }) => {
    switch ($noteSize) {
      case NoteSize.Open:
        return `
          height: calc(369px + ${measuredNoteHeaderHeight}px);
        `;
      case NoteSize.Maximized:
        return `
          height: calc(642px + ${measuredNoteHeaderHeight}px);
        `;
      case NoteSize.Minimized:
      default:
        return `
          height: ${measuredNoteHeaderHeight}px;
        `;
    }
  }};

  max-height: calc(100vh - 60px);

  border-top-right-radius: 8px;
  border-top-left-radius: 8px;
  background: ${WHITE};
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  border: 1px solid ${BORDER_GRAY};
  border-bottom: 0;

  transform: ${({ $open }) => ($open ? 'translateY(0)' : 'translateY(100%)')};
  transition:
    transform 0.25s ease-in-out,
    width 0.25s ease-in-out,
    height 0.25s ease-in-out;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid ${BORDER_GRAY};
  padding: 8px;
  z-index: 2;
`;
interface NoteViewProps {
  noteSize: NoteSize;
  handleMaximizeNote: () => void;
  handleOpenNote: () => void;
  handleCloseNote: () => void;
  note: NoteType;
  coachId: string | undefined;
  onDraft: (html: string) => void;
  onTitleChange: (title: string) => void;
  handleTextFocus: () => void;
  handleTitleFocus: () => void;
  handleGuidelinesButtonClick: () => void;
  handleOnSave: () => void;
  loading: boolean;
  isNewNote: boolean;
  isNoteEdit: boolean;
  hasDraft: boolean;
  loadingSave: boolean;
}

const NoteView: React.FC<NoteViewProps> = ({
  noteSize,
  loading,
  isNewNote,
  isNoteEdit,
  coachId,
  note,
  handleMaximizeNote,
  handleOpenNote,
  handleCloseNote,
  handleTextFocus,
  handleTitleFocus,
  handleGuidelinesButtonClick,
  handleOnSave,
  onDraft,
  onTitleChange,
  hasDraft,
  loadingSave,
}) => {
  const open = noteSize !== NoteSize.Closed;

  const hasEmptyFields = !note.content || !note.title;

  const saveIsDisabled = loading || !hasDraft || hasEmptyFields || loadingSave;

  const showEditor = !loading && (isNoteEdit || isNewNote);
  const showReader = !loading && !isNoteEdit && !isNewNote;

  const editorHeight =
    noteSize === NoteSize.Maximized
      ? EDITOR_HEIGHT.maximized
      : EDITOR_HEIGHT.default;
  const readerHeight =
    noteSize === NoteSize.Maximized
      ? READER_HEIGHT.maximized
      : READER_HEIGHT.default;

  return (
    <StyledNoteContainer
      $open={open}
      $noteSize={noteSize}
      data-testid="note-container"
    >
      <NoteHeader
        noteSize={noteSize}
        handleMaximizeNote={handleMaximizeNote}
        handleOpenNote={handleOpenNote}
        handleCloseNote={handleCloseNote}
        onTitleChange={onTitleChange}
        note={note}
        coachId={coachId}
        onTitleFocus={handleTitleFocus}
        loading={loading}
      />
      {loading && <LoadingPanel noBorder fullHeight />}
      {showEditor && (
        <>
          <RichTextEditor
            content={note.content ?? ''}
            onChange={onDraft}
            placeholder="Write here to leave a note"
            onFocus={handleTextFocus}
            height={editorHeight}
          />
          <ButtonContainer>
            <IconButton
              icon={<FontAwesomeIcon icon={faQuestionCircle} fontSize={16} />}
              size={ButtonSize.ExtraSmall}
              testId="notes-info-button"
              onClick={handleGuidelinesButtonClick}
            />
            <Button
              type="submit"
              variant={ButtonVariant.Dark}
              testId="note-save-button"
              disabled={saveIsDisabled}
              onClick={handleOnSave}
              loading={loadingSave}
            >
              Save
            </Button>
          </ButtonContainer>
        </>
      )}
      {showReader && (
        <RichTextReadOnly content={note.content ?? ''} height={readerHeight} />
      )}
    </StyledNoteContainer>
  );
};

const newNoteTemplate: NoteType = {
  id: undefined,
  content: '',
  title: 'Untitled Note',
};

export const Note: React.FC = () => {
  const { track } = useTracking();
  const {
    noteId,
    closeNote,
    setNoteSize,
    noteSize,
    setGuidelinesOpen,
    setNoteId,
  } = useNotesContext();
  const { memberId } = useParams();
  const [createNote, { loading: creatingNote }] =
    useMutation(CreateNoteDocument);
  const [updateNote, { loading: updatingNote }] =
    useMutation(UpdateNoteDocument);
  const [closeConfirmationModalIsOpen, setCloseConfirmationModalIsOpen] =
    useState(false);

  const [currentNote, setCurrentNote] = useState(newNoteTemplate);
  const { data: selfData, loading: loadingSelf } = useQuery(SelfDocument, {
    fetchPolicy: 'cache-first',
  });

  const coachId = selfData?.self?.id;

  // only allow editing if the note the coach is the author
  const isNoteEdit = !!coachId && currentNote.coach?.id === coachId;
  const isNewNote = !noteId;

  const { data, networkStatus } = useQuery(NoteByIdDocument, {
    variables: {
      id: noteId ?? '',
    },
    skip: isNewNote,
    notifyOnNetworkStatusChange: true,
  });

  // we only want to show the loading state if we are fetching the note on the first load
  const loadingNote = networkStatus === NetworkStatus.loading;

  const getHasDraft = useCallback(() => {
    const { content, title } = currentNote;

    // is new note and has content and title
    if (isNewNote) {
      return !!content || (title !== newNoteTemplate.title && title !== '');
    }

    // is edit note, has content and title, and one or both of them are edited.
    if (isNoteEdit && content && title) {
      return (
        currentNote.content !== data?.note?.content ||
        currentNote.title !== data?.note?.title
      );
    }

    return false;
  }, [
    currentNote,
    data?.note?.content,
    data?.note?.title,
    isNewNote,
    isNoteEdit,
  ]);

  const hasDraft = getHasDraft();

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasDraft && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (data?.note) {
      setCurrentNote(data.note);
    } else {
      setCurrentNote(newNoteTemplate);
    }
  }, [data?.note]);

  // Used to ensure a slow save doesnt overwrite a newly-opened note
  const activeNoteIdRef = useRef(noteId);
  useEffect(() => {
    activeNoteIdRef.current = noteId;
  }, [noteId]);

  const handleMaximizeNote = () => {
    if (noteSize === NoteSize.Maximized) {
      track(EventNames.ExpansionClick, {
        event_category: EventCategories.Notes,
        event_subcategory: EventSubCategories.Collapse,
        note_id: noteId ?? undefined,
        member_id: memberId,
      });
      setNoteSize(NoteSize.Open);
      return;
    }
    track(EventNames.ExpansionClick, {
      event_category: EventCategories.Notes,
      event_subcategory: EventSubCategories.Expand,
      note_id: noteId ?? undefined,
      member_id: memberId,
    });
    setNoteSize(NoteSize.Maximized);
  };

  const handleOpenNote = () => {
    if (noteSize === NoteSize.Minimized) {
      setNoteSize(NoteSize.Open);
      track(EventNames.CaretClick, {
        event_category: EventCategories.Notes,
        event_subcategory: EventSubCategories.Expand,
        note_id: noteId ?? undefined,
        member_id: memberId,
      });

      return;
    }
    setNoteSize(NoteSize.Minimized);
    track(EventNames.CaretClick, {
      event_category: EventCategories.Notes,
      event_subcategory: EventSubCategories.Collapse,
      note_id: noteId ?? undefined,
      member_id: memberId,
    });
  };

  const closeCurrentNote = () => {
    setCurrentNote(newNoteTemplate);
    setCloseConfirmationModalIsOpen(false);
    closeNote();
  };

  const handleCloseNote = () => {
    track(EventNames.CloseClick, {
      event_category: EventCategories.Notes,
      event_subcategory: EventSubCategories.MemberNote,
      note_id: noteId ?? undefined,
      member_id: memberId,
    });

    if (hasDraft) {
      setCloseConfirmationModalIsOpen(true);
      return;
    }
    closeCurrentNote();
  };

  const onConfirmClose = () => {
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
    closeCurrentNote();
  };

  const onCancelClose = () => {
    setCloseConfirmationModalIsOpen(false);
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  };

  const onDraft = (html: string) => {
    setCurrentNote({
      ...currentNote,
      content: html,
    });
  };

  const onTitleChange = (title: string) => {
    setCurrentNote({
      ...currentNote,
      title,
    });
  };

  const handleTextFocus = () => {
    track(EventNames.TextBoxActivated, {
      member_id: memberId,
      event_category: EventCategories.Notes,
      note_id: noteId ?? undefined,
      event_subcategory: EventSubCategories.Body,
    });
  };

  const handleTitleFocus = () => {
    track(EventNames.TextBoxActivated, {
      member_id: memberId,
      event_category: EventCategories.Notes,
      note_id: noteId ?? undefined,
      event_subcategory: EventSubCategories.Title,
    });
  };

  const handleGuidelinesButtonClick = () => {
    track(EventNames.InformationIconClick, {
      member_id: memberId,
      event_category: EventCategories.Notes,
      note_id: noteId ?? undefined,
      event_subcategory: EventSubCategories.Information,
    });
    setGuidelinesOpen(true);
  };

  const handleOnSave = async () => {
    const { title, content } = currentNote;

    if (!memberId || !content || !title) {
      return;
    }

    track(EventNames.ButtonClick, {
      event_category: EventCategories.Notes,
      event_label: EventLabels.Save,
      note_id: currentNote.id,
      member_id: memberId,
      screen_name: ScreenNames.MemberProfile,
    });

    if (isNewNote) {
      const newNote = await createNote({
        variables: {
          input: {
            memberId,
            content,
            title,
          },
        },
        update: (cache, { data: newData }) => {
          if (newData?.createNote.note) {
            updateCache(cache, newData?.createNote.note, memberId);
          }
        },
      });
      // Only adopt the created note if the coach is still on the new-note view.
      // If they switched to another note while the create was in flight, don't
      // hijack their current selection.
      if (newNote.data?.createNote.note && activeNoteIdRef.current === noteId) {
        setCurrentNote(newNote.data?.createNote.note);
        setNoteId(newNote.data?.createNote.note.id);
      }

      return;
    }

    // use the note id from the current note state, not the context,
    // to avoid a race condition where the note content gets clobbered
    if (!currentNote.id) {
      return;
    }

    await updateNote({
      variables: {
        input: {
          id: currentNote.id,
          content,
          title,
        },
      },
    });
  };

  useBeforeUnload((event) => {
    if (hasDraft) {
      event.preventDefault(); // this will prompt the user to stay on the page using the default browser dialog
    }
  });

  return (
    <>
      <NoteView
        noteSize={noteSize}
        handleMaximizeNote={handleMaximizeNote}
        handleOpenNote={handleOpenNote}
        handleCloseNote={handleCloseNote}
        note={currentNote}
        coachId={coachId}
        onDraft={onDraft}
        onTitleChange={onTitleChange}
        handleTextFocus={handleTextFocus}
        handleTitleFocus={handleTitleFocus}
        handleOnSave={handleOnSave}
        handleGuidelinesButtonClick={handleGuidelinesButtonClick}
        loading={loadingNote || loadingSelf}
        isNewNote={isNewNote}
        isNoteEdit={isNoteEdit}
        hasDraft={hasDraft}
        loadingSave={creatingNote || updatingNote}
      />
      <ConfirmationModal
        isOpen={closeConfirmationModalIsOpen || blocker.state === 'blocked'}
        onConfirm={onConfirmClose}
        confirmLabel="Close"
        cancelLabel="Nevermind"
        description="You are about to discard your unsaved changes."
        title="Are you sure you want to close?"
        onClose={onCancelClose}
        trackingData={{
          event_category: EventCategories.Notes,
          note_id: noteId ?? undefined,
          member_id: memberId,
          card_name: EventProfileCardNames.CancelConfirmModal,
          screen_name: ScreenNames.MemberProfile,
        }}
      />
    </>
  );
};
